import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "epinio-app-shell" }
const _hoisted_2 = { class: "dock-tab-toolbar" }
const _hoisted_3 = ["value", "options"]
const _hoisted_4 = { class: "pull-left ml-5" }
const _hoisted_5 = { class: "status pull-left" }
const _hoisted_6 = {
  key: 0,
  class: "text-success"
}
const _hoisted_7 = {
  key: 1,
  class: "text-warning"
}
const _hoisted_8 = {
  key: 2,
  class: "text-error"
}
const _hoisted_9 = { class: "dock-tab-body" }

import { useStore } from 'vuex';
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  PropType,
} from 'vue';

import Socket, {
  EVENT_CONNECTED,
  EVENT_CONNECTING,
  EVENT_DISCONNECTED,
  EVENT_MESSAGE,
  EVENT_CONNECT_ERROR,
} from '../utils/socket';
import { allHash, addParams, base64Decode, base64Encode } from '../utils/browser';
import { useApplicationSocketMixin } from './ApplicationSocketMixin';


export default /*@__PURE__*/_defineComponent({
  __name: 'ApplicationShell',
  props: {
  application: {
    type: Object as PropType<object>,
    default: null,
  },
  endpoint: {
    type: String as PropType<string>,
    default: '',
  },
  initialInstance: {
    type: String,
    default: null,
  },
},
  setup(__props) {

const store = useStore();
const t = store.getters['i18n/t'];

const props = __props;

const {
  socket,
  isOpen,
  backlog,
  instanceChoices,
  getRootSocketUrl,
} = useApplicationSocketMixin(props);

const xterm = ref<HTMLElement | null>(null);
const instance = ref<string>(props.initialInstance || instanceChoices.value[0]);
const instanceOptions = computed(() => instanceChoices.value.map((choice: string) => ({ label: choice, value: choice })));
const terminal = ref<any>(null);
const fitAddon = ref<any>(null);
const searchAddon = ref<any>(null);
const webglAddon = ref<any>(null);
const isOpening = ref<boolean>(false);
const keepAliveTimer = ref<object | null>(null);
const xtermConfig = {
  allowProposedApi: true,
  cursorBlink:      true,
  useStyle:         true,
  fontSize:         12,
};

let themeObserver: MutationObserver | null = null;
let resizeObserver: ResizeObserver | null = null;

// xterm reads these CSS vars once at construction and bakes them into its
// own theme object, it doesn't react to CSS changes the way the rest of the
// UI does. Without this, switching light/dark mode leaves an open terminal
// stuck on whatever colors it started with.
const getTerminalTheme = () => {
  const docStyle = getComputedStyle(document.body);

  return {
    background:          docStyle.getPropertyValue('--terminal-bg').trim(),
    foreground:          docStyle.getPropertyValue('--terminal-text').trim(),
    cursor:              docStyle.getPropertyValue('--terminal-cursor').trim(),
    selectionBackground: docStyle.getPropertyValue('--terminal-selection').trim(),
  };
};

watch(
  () => instance.value,
  () => {
    connect();
  }
);

onBeforeUnmount(() => {
  clearInterval(keepAliveTimer.value);
  cleanup();
});

onMounted(async () => {
  await setupTerminal();
  await connect();

  isOpen.value = true;

  clearInterval(keepAliveTimer.value);
  keepAliveTimer.value = setInterval(() => {
    fit();
  }, 60 * 1000);
}); 

const setupTerminal = async () => {
  const xtermLib = await import('xterm');

  const addons = await allHash({
    fit:      import('xterm-addon-fit'),
    webgl:    import('xterm-addon-webgl'),
    weblinks: import('xterm-addon-web-links'),
    search:   import('xterm-addon-search'),
  });

  const terminalTemp = new xtermLib.Terminal({
    theme: getTerminalTheme(),
    ...xtermConfig,
  });

  fitAddon.value = new addons.fit.FitAddon();
  searchAddon.value = new addons.search.SearchAddon();

  try {
    webglAddon.value = new addons.webgl.WebglAddon();
  } catch (e: any) { // eslint-disable-line @typescript-eslint/no-unused-vars
    // Some browsers (Safari) don't support the webgl renderer, so don't use it.
    webglAddon.value = null;
  }

  terminalTemp.loadAddon(fitAddon.value);
  terminalTemp.loadAddon(searchAddon.value);
  terminalTemp.loadAddon(new addons.weblinks.WebLinksAddon());
  if (webglAddon.value) {
    terminalTemp.loadAddon(webglAddon.value);
  }
  
  terminalTemp.open(xterm.value);

  resizeObserver = new ResizeObserver(() => fit());
  resizeObserver.observe(xterm.value as HTMLElement);

  fit();
  flush();

  terminalTemp.onData((input) => {
    const msg = `0${ base64Encode(input) }`;

    write(msg);
  });

  terminal.value = terminalTemp;

  themeObserver = new MutationObserver(() => {
    if (terminal.value) {
      terminal.value.options.theme = getTerminalTheme();
    }
  });
  themeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
};

const write = (msg) => {
  if (isOpen.value) {
    socket.value.send(msg);
  } else {
    backlog.value.push(msg);
  }
};

const clear = () => {
  terminal.value.clear();
};

const getSocketUrl = async () => {
  const { url, token } = await getRootSocketUrl();

  return addParams(url, {
    authtoken: token,
    instance: instance.value,
  });
};

const connect = async () => {
  if (socket.value) {
    await socket.value.disconnect();
    socket.value = null;
    terminal.value.reset();
  }

  const url = await getSocketUrl();

  if (!url) {
    return;
  }

  socket.value = new Socket(url, false, 0, 'base64.channel.k8s.io');

  socket.value.addEventListener(EVENT_CONNECTING, () => {
    isOpen.value = false;
    isOpening.value = true;
  });

  socket.value.addEventListener(EVENT_CONNECT_ERROR, (e) => {
    isOpen.value = false;
    isOpening.value = false;
    console.error('Connect Error', e);
  });

  socket.value.addEventListener(EVENT_CONNECTED, () => {
    isOpen.value = true;
    isOpening.value = false;
    fit();
    flush();
  });

  socket.value.addEventListener(EVENT_DISCONNECTED, () => {
    isOpen.value = false;
    isOpening.value = false;
  });

  socket.value.addEventListener(EVENT_MESSAGE, (e) => {
    const type = e.detail.data.substr(0, 1);
    const msg = base64Decode(e.detail.data.substr(1));

    // Kubelet exec WS channels: 1=stdout, 2=stderr, 3=error/status, 4=resize.
    // Render both stdout and stderr in the terminal; only surface channel 3
    // (proxy/status messages) to the console.
    if (type === '1' || type === '2') {
      terminal.value.write(msg);
    } else {
      console.error(msg);
    }
  });

  socket.value.connect();
  terminal.value.focus();
};

const flush = () => {
  const backlogTemp = backlog.value.slice();

  backlog.value = [];

  for (const data of backlogTemp) {
    socket.value.send(data);
  }
};

const fit = () => {
  if (!fitAddon.value) {
    return;
  }
  
  fitAddon.value.fit();
  const { rows, cols } = fitAddon.value.proposeDimensions();

  if (!isOpen.value) {
    return;
  }

  const message = `4${ base64Encode(
    JSON.stringify({
      Width:  Math.floor(cols),
      Height: Math.floor(rows),
    })
  ) }`;

  socket.value.send(message);
};

const cleanup = () => {
  themeObserver?.disconnect();
  themeObserver = null;

  resizeObserver?.disconnect();
  resizeObserver = null;

  if (socket.value) {
    socket.value.disconnect();
    socket.value = null;
  }

  if (terminal.value) {
    terminal.value.dispose();
    terminal.value = null;
  }
};

return (_ctx: any,_cache: any) => {
  const _directive_clean_html = _resolveDirective("clean-html")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (_unref(instanceChoices).length > 1)
        ? (_openBlock(), _createElementBlock("trailhand-dropdown", {
            key: 0,
            class: "pull-left",
            value: instance.value,
            options: instanceOptions.value,
            size: "small",
            position: "top",
            onDropdownChange: _cache[0] || (_cache[0] = (e) => instance.value = e.detail.value)
          }, null, 40, _hoisted_3))
        : _createCommentVNode("", true),
      _cache[1] || (_cache[1] = _createTextVNode()),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("trailhand-button", {
          size: "small",
          onClick: clear
        }, _toDisplayString(_unref(t)('wm.containerShell.clear')), 1)
      ]),
      _cache[2] || (_cache[2] = _createTextVNode()),
      _createElementVNode("div", _hoisted_5, [
        (_unref(isOpen))
          ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(_unref(t)('wm.connection.connected')), 1))
          : (isOpening.value)
            ? _withDirectives((_openBlock(), _createElementBlock("span", _hoisted_7, null, 512)), [
                [_directive_clean_html, _unref(t)('wm.connection.connecting')]
              ])
            : (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(_unref(t)('wm.connection.disconnected')), 1))
      ])
    ]),
    _cache[3] || (_cache[3] = _createTextVNode()),
    _createElementVNode("div", _hoisted_9, [
      _createElementVNode("div", {
        class: _normalizeClass(["shell-container", { open: _unref(isOpen), closed: !_unref(isOpen) }])
      }, [
        _createElementVNode("div", {
          ref_key: "xterm",
          ref: xterm,
          class: "shell-body"
        }, null, 512)
      ], 2)
    ])
  ]))
}
}

})